import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMutationData } from "@hooks/useMutationData";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant, { showErrorToast, showWarningToast } from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { extractErrorCode } from "@utils/error";

const showImportApiErrorToast = (err, t) => {
  const i18nKey = extractErrorCode(err, "toast.invalid-file");
  showErrorToast(t("common.error"), t(i18nKey));
};

const useImportFileVulnerabilityPolicy = () => {
  const { t } = useTranslation();

  // Use useMutationData to call API import
  const { mutate, isLoading, error } = useMutationData(
    queryKeys.vulnerabilityPolicyImport,
    {
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data", // Send file
      },
    },
  );

  const importFile = useCallback(
    async (file) => {
      try {
        // Check file
        if (!file) {
          throw new Error(t("license-policy.no-file-selected"));
        }
        if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
          throw new Error(t("license-policy.invalid-file-format"));
        }

        // Create FormData to send file
        const formData = new FormData();
        formData.append("file", file);

        // Call API import
        const response = await new Promise((resolve, reject) => {
          mutate(formData, {
            onSuccess: (response) => {
              const inUseLevelCodes = response?.data?.inUseLevelCodes;
              if (inUseLevelCodes) {
                showWarningToast(
                  t("common.warning"),
                  t("vulnerability-policy.vulnerability-policies-import-error-conflict", {
                    levelCode: inUseLevelCodes,
                  }),
                );
              }
              resolve(response);
            },
            onError: reject,
          });
        });

        ToastConstant(TOAST.success, t("license-policy.file-imported-successfully"));

        return response;
      } catch (err) {
        if (err?.response) {
          showImportApiErrorToast(err, t);
        } else {
          showErrorToast(t("common.error"), err?.message || t("toast.invalid-file"));
        }
        console.error("Import error:", err);
        throw err;
      }
    },
    [t, mutate],
  );

  return { importFile, isLoading, error };
};

export default useImportFileVulnerabilityPolicy;
